// src/components/LAB/LABAthleteView.js
// Vista analítica LAB — mockup Primer Umbral
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Chart, registerables } from 'chart.js';
import API_BASE_URL from '../../config/api';
import LABCalendarView from './LABCalendarView';
import useFetchEvents from '../hooks/useFetchEvents';
import { useTranslation } from '../../i18n';
import AthleteProfile from '../AthleteProfile/AthleteProfile';
import WellnessVsCargaCard from '../TrainerDashboard/WellnessVsCarga/WellnessVsCargaCard';

Chart.register(...registerables);

const API = (process.env.REACT_APP_API_URL || API_BASE_URL).replace(/\/+$/, '');
const PU = '#193255';
const ORANGE = '#f7941d';

const ZONE_COLORS = ['#10b981','#059669','#f59e0b','#ef4444','#dc2626'];
const SPORT_COLORS = { correr:'#9C27B0', ciclismo:'#4CAF50', fuerza:'#f7941d', nadar:'#2196F3', caminar:'#9ca3af', otro:'#6b7280' };

function useFetch(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!url) return;
    const token = localStorage.getItem('token');
    setLoading(true);
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [url]);
  return { data, loading };
}

function MetricCard({ label, value, color, sub }) {
  return (
    <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 14px', flex:1, minWidth:90 }}>
      <div style={{ fontSize:22, fontWeight:600, color: color || '#111827' }}>{value ?? '-'}</div>
      <div style={{ fontSize:10, color:'#6b7280', marginTop:2 }}>{label}</div>
      {sub && <div style={{ fontSize:10, color, marginTop:4 }}>{sub}</div>}
    </div>
  );
}

function ZoneBar({ label, pct, min, color }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
      <span style={{ fontSize:11, color, width:32, flexShrink:0 }}>{label}</span>
      <div style={{ flex:1, background:'#f0f0f0', borderRadius:99, height:10, overflow:'hidden' }}>
        <div style={{ width:`${pct}%`, height:'100%', background:color, borderRadius:99 }} />
      </div>
      <span style={{ fontSize:10, color, width:36, textAlign:'right', flexShrink:0 }}>{pct.toFixed(1)}%</span>
      <span style={{ fontSize:10, color:'#6b7280', width:55, textAlign:'right', flexShrink:0 }}>{min} min</span>
    </div>
  );
}

export default function LABAthleteView({ athleteId, athleteName, athletePhoto }) {
  const [tab, setTab] = useState('dashboard');
  const { language } = useTranslation();
  const L = language === 'en' ? {
    tabs: { dashboard:'Dashboard', calendar:'Calendar', profile:'Profile', pmc:'PMC', zones:'HR Zones', wellness:'Wellness', ia:'AI', banister:'Banister', competitions:'Competitions' },
    currentState:'Current State · Season', fitness:'CTL (Fitness)', fatigue:'ATL (Fatigue)', form:'TSB (Form)', tssTotal:'TSS Total',
    formPositive:'✓ Positive form — ready to compete', formNeutral:'→ Neutral form', formFatigue:'⚠ High fatigue — recovery recommended',
    weeklyTss:'Weekly TSS — Last 8 weeks', seasonSummary:'Season summary',
    kmRun:'km run', kmBike:'km biked', mSwim:'m swum', hoursRun:'Hours running', hoursBike:'Hours cycling', hoursSwim:'Hours swimming', hoursStrength:'Hours strength',
    pmcTitle:'Performance Management Chart — Last 8 weeks', pmcInterpretation:'PMC Interpretation',
    ctlCurrent:'Current CTL', atlCurrent:'Current ATL', tsbCurrent:'Current TSB',
    hrDistribution:'HR Distribution — Full season', hrAvgBySport:'Avg HR by sport', hrChart:'Chart distribution',
    monitorForm:'Monitor form.', positiveForm:'Positive form.',
    wellnessTitle:'Wellness Log', today:'Today',
    sleepQuality:'Sleep quality', energy:'Energy', muscleTension:'Muscle tension', mentalState:'Mental state',
    restHR:'Resting HR', perceivedStress:'Perceived stress', wellnessScore:'Wellness Score', moderate:'Moderate — monitor',
    banisterTitle:'Banister Model — Fitness-Fatigue', ctlFitness:'CTL Fitness', atlFatigue:'ATL Fatigue', tsbForm:'TSB Form',
    competitionsTitle:'Scheduled competitions', noCompetitions:'No scheduled competitions',
    compHeaders:['Date','Event','Sport','Distance','Priority','City','Target'],
    iaTitle:'AI Analysis · Primer Umbral LAB', iaBasedOn:'Analysis based on current data.', iaForm:'Form:',
    coachReport:'Coach report', generateReport:'Generate analysis', generating:'Generating...',
    reportPlaceholder:'Press "Generate analysis" to get an interpretive report based on athlete data.',
    askIA:'Ask the AI', noPmcData:'Not enough PMC data. Import activities and recalculate PMC.',
    noBanisterData:'Not enough data for Banister model. Import activities and recalculate PMC.',
    running:'running', cycling:'cycling', strength:'strength', swimming:'swimming', walking:'walking',
    correr:'Running', ciclismo:'Cycling', fuerza:'Strength', nadar:'Swimming', caminar:'Walking',
    wellnessGood:'✓ Good — continue with plan', wellnessLow:'⚠ Low — adjust load',
  } : {
    tabs: { dashboard:'Dashboard', calendar:'Calendario', profile:'Perfil', pmc:'PMC', zones:'Zonas HR', wellness:'Wellness', ia:'IA', banister:'Banister', competitions:'Competencia' },
    currentState:'Estado Actual · Temporada', fitness:'CTL (Fitness)', fatigue:'ATL (Fatiga)', form:'TSB (Forma)', tssTotal:'TSS Total',
    formPositive:'✓ Forma positiva — apto para competencia', formNeutral:'→ Forma neutral', formFatigue:'⚠ Fatiga alta — recuperación recomendada',
    weeklyTss:'TSS semanal últimas 8 semanas', seasonSummary:'Resumen temporada',
    kmRun:'km corridos', kmBike:'km en bici', mSwim:'m nadados', hoursRun:'Horas running', hoursBike:'Horas bici', hoursSwim:'Horas natación', hoursStrength:'Horas fuerza',
    pmcTitle:'Performance Management Chart — últimas 8 semanas', pmcInterpretation:'Interpretación PMC',
    ctlCurrent:'CTL actual', atlCurrent:'ATL actual', tsbCurrent:'TSB actual',
    hrDistribution:'Distribución HR — temporada completa', hrAvgBySport:'FC promedio por disciplina', hrChart:'Distribución en gráfico',
    monitorForm:'Monitorear forma.', positiveForm:'Forma positiva.',
    wellnessTitle:'Registro Wellness', today:'Hoy',
    sleepQuality:'Calidad de sueño', energy:'Energía', muscleTension:'Tensión muscular', mentalState:'Estado mental',
    restHR:'FC reposo', perceivedStress:'Estrés percibido', wellnessScore:'Score Wellness', moderate:'Moderado — monitorear',
    banisterTitle:'Modelo Banister — Fitness-Fatiga', ctlFitness:'CTL Fitness', atlFatigue:'ATL Fatiga', tsbForm:'TSB Forma',
    competitionsTitle:'Competencias agendadas', noCompetitions:'No hay competencias agendadas',
    compHeaders:['Fecha','Evento','Deporte','Distancia','Prioridad','Ciudad','Objetivo'],
    iaTitle:'Análisis IA · Primer Umbral LAB', iaBasedOn:'Análisis basado en datos actuales.', iaForm:'Forma:',
    coachReport:'Reporte para el coach', generateReport:'Generar análisis', generating:'Generando...',
    reportPlaceholder:'Presiona "Generar análisis" para obtener un reporte interpretativo basado en los datos del atleta.',
    askIA:'Preguntarle a la IA', noPmcData:'Sin datos PMC suficientes. Importa actividades y recalcula el PMC.',
    noBanisterData:'Sin datos suficientes para el modelo Banister. Importa actividades y recalcula el PMC.',
    running:'running', cycling:'cycling', strength:'strength', swimming:'swimming', walking:'walking',
    correr:'correr', ciclismo:'ciclismo', fuerza:'fuerza', nadar:'nadar', caminar:'caminar',
    wellnessGood:'✓ Bien — continuar con plan', wellnessLow:'⚠ Bajo — ajustar carga',
  };
  const [wellness, setWellness] = useState({ sleep:7, energy:6, muscle:4, mental:8, hr:52, stress:3 });
  const [iaInput, setIaInput] = useState('');
  const { /* events: calEvents, setEvents: setCalEvents, fetchEvents */ } = useFetchEvents(athleteId, null); // eslint-disable-line no-unused-vars

  // eslint-disable-next-line no-unused-vars
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  // eslint-disable-next-line no-unused-vars
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  // eslint-disable-next-line no-unused-vars
  const [selDay, setSelDay] = useState(null);
  const [iaMsgs, setIaMsgs] = useState([]);
  const [iaLoading, setIaLoading] = useState(false);
  const [coachReport, setCoachReport] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState('');
  const sendReportEmail = async () => {
    setEmailSending(true); setEmailSent('');
    try {
      const res = await fetch(`${API}/api/ia/send-report/${athleteId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from: reportFrom, to: reportTo })
      });
      const data = await res.json();
      setEmailSent(data.ok ? `✓ ${language === 'en' ? 'Sent to' : 'Enviado a'} ${data.sentTo}` : data.message || 'Error');
    } catch(e) { setEmailSent(language === 'en' ? 'Error sending email' : 'Error al enviar'); }
    setEmailSending(false);
  };
  const [reportFrom, setReportFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 56); return d.toISOString().slice(0,10); });
  const [reportTo, setReportTo] = useState(() => new Date().toISOString().slice(0,10));
  const generateReport = async () => {
    setReportLoading(true); setCoachReport('');
    try {
      const res = await fetch(`${API}/api/ia/coach-report/${athleteId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ from: reportFrom, to: reportTo }) });
      const data = await res.json();
      setCoachReport(data.report || 'Sin respuesta');
    } catch(e) { setCoachReport('Error al generar el reporte.'); }
    setReportLoading(false);
  };
  const [competitions, setCompetitions] = useState([]);

  // Fetch competencias cuando se abre el tab
  React.useEffect(() => {
    if (tab === 'competitions' && athleteId) {
      fetch(`${API}/api/activities/athlete/${athleteId}?start=2026-01-01T00:00:00.000Z&end=2027-01-01T00:00:00.000Z`)
        .then(r => r.json())
        .then(d => {
          const arr = Array.isArray(d) ? d : [];
          setCompetitions(arr.filter(a => a.type === 'competencia' || a.isCompetition));
        })
        .catch(() => setCompetitions([]));
    }
  }, [tab, athleteId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Charts refs
  const weeklyRef = useRef(); const pmcRef = useRef(); const zonesRef = useRef();
  const wellnessRef = useRef(); const banisterRef = useRef();
  const weeklyChart = useRef(); const pmcChart = useRef(); const zonesChart = useRef();
  const wellnessChart = useRef(); const banisterChart = useRef();

  // Data
  const { data: pmcData } = useFetch(athleteId ? `${API}/api/performance/pmc/${athleteId}` : null);
  const { data: activitiesData } = useFetch(athleteId ? `${API}/api/activities/athlete/${athleteId}` : null);

  const activities = Array.isArray(activitiesData) ? activitiesData : [];
  const pmcArr = Array.isArray(pmcData) ? pmcData : [];

  // Últimos valores PMC
  const lastPmc = pmcArr.length ? pmcArr[pmcArr.length - 1] : null;
  const ctl = lastPmc ? Math.round(lastPmc.ctl * 10) / 10 : '-';
  const atl = lastPmc ? Math.round(lastPmc.atl * 10) / 10 : '-';
  const tsb = lastPmc ? Math.round((lastPmc.ctl - lastPmc.atl) * 10) / 10 : '-';

  // TSS total temporada
  const tssTotal = activities.reduce((s, a) => s + (a.tss || a.completed?.tss || 0), 0);

  // Totales temporada por deporte (distancia y horas)
  const seasonTotals = (() => {
    const t = { runKm:0, bikeKm:0, swimM:0, runH:0, bikeH:0, swimH:0, strengthH:0 };
    const toHours = (dur) => {
      if (!dur) return 0;
      if (typeof dur === 'number') return dur / 3600;
      const p = String(dur).split(':').map(Number);
      if (p.length === 3 && !p.some(isNaN)) return p[0] + p[1]/60 + p[2]/3600;
      if (p.length === 2 && !p.some(isNaN)) return p[0]/60 + p[1]/3600;
      return 0;
    };
    activities.forEach(a => {
      const dist = parseFloat(a.completed?.distance ?? a.completed?.distancia) || 0;
      const h = toHours(a.completed?.duration);
      if (a.type === 'correr') { t.runKm += dist; t.runH += h; }
      else if (a.type === 'ciclismo') { t.bikeKm += dist; t.bikeH += h; }
      else if (a.type === 'nadar') { t.swimM += dist > 100 ? dist : dist * 1000; t.swimH += h; }
      else if (a.type === 'fuerza') { t.strengthH += h; }
    });
    return t;
  })();

  // {L.weeklyTss}} por deporte
  const weeklyTss = () => {
    // Lunes de la semana actual (ISO)
    const today = new Date(); today.setHours(0,0,0,0);
    const dow = (today.getDay() + 6) % 7; // 0 = lunes
    const mondayThisWeek = new Date(today); mondayThisWeek.setDate(today.getDate() - dow);
    const weeks = Array.from({ length: 8 }, (_, i) => {
      const start = new Date(mondayThisWeek); start.setDate(start.getDate() - (7 - i - 1) * 7);
      const end = new Date(start); end.setDate(end.getDate() + 7);
      return { start, end, label: `S-${7-i}` };
    });

    const sports = ['correr','ciclismo','fuerza','nadar','caminar'];
    return {
      labels: weeks.map(w => w.label),
      datasets: sports.map(sport => ({
        label: sport,
        data: weeks.map(w => {
          return activities
            .filter(a => {
              const d = new Date(a.date || a.start);
              return d >= w.start && d < w.end && (a.type === sport || (a.sport === sport));
            })
            .reduce((s, a) => s + (a.tss || a.completed?.tss || 0), 0);
        }),
        backgroundColor: SPORT_COLORS[sport],
        stack: 'tss',
      }))
    };
  };

  // Zonas HR acumuladas
  const hrZonesSummary = () => {
    const totals = { z1:0, z2:0, z3:0, z4:0, z5:0 };
    activities.forEach(a => {
      if (a.hrZones) {
        totals.z1 += a.hrZones.z1 || 0;
        totals.z2 += a.hrZones.z2 || 0;
        totals.z3 += a.hrZones.z3 || 0;
        totals.z4 += a.hrZones.z4 || 0;
        totals.z5 += a.hrZones.z5 || 0;
      }
    });
    const total = Object.values(totals).reduce((a,b) => a+b, 0) || 1;
    return Object.entries(totals).map(([k, v]) => ({
      label: k.toUpperCase(), min: Math.round(v), pct: (v / total * 100)
    }));
  };

  // HR promedio por deporte
  const hrBySport = () => {
    const sports = ['correr','ciclismo','fuerza','nadar'];
    return sports.map(s => {
      const acts = activities.filter(a => (a.type === s || a.sport === s) && (a.completed?.avgHeartRate || a.heartRateAvg));
      const avg = acts.length ? Math.round(acts.reduce((sum, a) => sum + (a.completed?.avgHeartRate || a.heartRateAvg || 0), 0) / acts.length) : '-';
      return { sport: s, avg };
    });
  };

  // Wellness score
  const wellnessScore = () => {
    const { sleep, energy, muscle, mental, hr, stress } = wellness;
    const hrScore = Math.max(0, 10 - (hr - 40) / 4);
    return Math.round((sleep + energy + (10-muscle) + mental + hrScore + (10-stress)) / 6 * 10);
  };

  // Charts
  useEffect(() => {
    if (tab === 'dashboard' && weeklyRef.current) {
      weeklyChart.current?.destroy();
      const wd = weeklyTss();
      weeklyChart.current = new Chart(weeklyRef.current, {
        type: 'bar',
        data: wd,
        options: { responsive:true, maintainAspectRatio:false, plugins:{ legend:{ display:false } }, scales:{ x:{ stacked:true }, y:{ stacked:true, beginAtZero:true } } }
      });
    }
  }, [tab, activities.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === 'pmc' && pmcRef.current && pmcArr.length) {
      pmcChart.current?.destroy();
      const slice = pmcArr.slice(-56); // 8 semanas
      const labels = slice.map(p => new Date(p.date).toLocaleDateString('es-CL', { month:'2-digit', day:'2-digit' }));
      // TSS diario desde activities, indexado por fecha (YYYY-MM-DD)
      const tssByDay = {};
      activities.forEach(a => {
        const d = String(a.date || a.start || '').slice(0, 10);
        if (!d) return;
        tssByDay[d] = (tssByDay[d] || 0) + (Number(a.tss || a.completed?.tss) || 0);
      });
      const dailyTss = slice.map(p => Math.round(tssByDay[String(p.date).slice(0,10)] || 0));
      pmcChart.current = new Chart(pmcRef.current, {
        data: {
          labels,
          datasets: [
            { type:'line', label:'CTL (Fitness/42d)', data: slice.map(p=>p.ctl), borderColor:'#2a78d6', borderWidth:2, pointRadius:0, tension:0.3, yAxisID:'y' },
            { type:'line', label:'ATL (Fatiga/7d)',   data: slice.map(p=>p.atl), borderColor:'#e34948', borderWidth:2, pointRadius:0, tension:0.3, yAxisID:'y' },
            { type:'line', label:'TSB (Forma)',       data: slice.map(p=>p.ctl-p.atl), borderColor:'#1baf7a', borderWidth:2, borderDash:[4,3], pointRadius:0, tension:0.3, yAxisID:'y' },
            { type:'bar',  label:'TSS diario',        data: dailyTss, backgroundColor:'rgba(245, 158, 11, 0.35)', yAxisID:'y1' },
          ]
        },
        options: {
          responsive:true, maintainAspectRatio:false,
          plugins:{ legend:{ labels:{ boxWidth:12, font:{ size:10 } } } },
          scales: {
            y:  { position:'left' },
            y1: { position:'right', beginAtZero:true, grid:{ drawOnChartArea:false } }
          }
        }
      });
    }
  }, [tab, pmcArr.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === 'zones' && zonesRef.current) {
      zonesChart.current?.destroy();
      const zones = hrZonesSummary();
      zonesChart.current = new Chart(zonesRef.current, {
        type: 'doughnut',
        data: {
          labels: zones.map(z => z.label),
          datasets: [{ data: zones.map(z => z.min), backgroundColor: ZONE_COLORS, borderWidth:0 }]
        },
        options: { responsive:true, maintainAspectRatio:false, plugins:{ legend:{ position:'right', labels:{ boxWidth:10, font:{ size:10 } } } } }
      });
    }
  }, [tab, activities.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === 'wellness' && wellnessRef.current) {
      wellnessChart.current?.destroy();
      const history = [68, 72, 65, 70, 74, 71, wellnessScore()];
      wellnessChart.current = new Chart(wellnessRef.current, {
        type: 'line',
        data: {
          labels: ['L','M','X','J','V','S','H'],
          datasets: [{
            data: history,
            borderColor: ORANGE,
            backgroundColor: ORANGE + '22',
            borderWidth: 2,
            pointRadius: 4,
            pointBackgroundColor: ORANGE,
            fill: true,
            tension: 0.3,
          }]
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { y: { min: 0, max: 100, beginAtZero: true } }
        }
      });
    }
  }, [tab, wellness]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === 'banister' && banisterRef.current && pmcArr.length) {
      banisterChart.current?.destroy();
      const slice = pmcArr.slice(-90);
      const labels = slice.map(p => new Date(p.date).toLocaleDateString('es-CL', { month:'short', day:'numeric' }));
      banisterChart.current = new Chart(banisterRef.current, {
        type: 'line',
        data: {
          labels,
          datasets: [
            { label:'CTL', data: slice.map(p=>p.ctl), borderColor:'#2a78d6', borderWidth:2, pointRadius:0, tension:0.3 },
            { label:'ATL', data: slice.map(p=>p.atl), borderColor:'#e34948', borderWidth:2, pointRadius:0, tension:0.3 },
            { label:'TSB', data: slice.map(p=>p.ctl-p.atl), borderColor:'#1baf7a', borderWidth:2, pointRadius:0, tension:0.3 },
          ]
        },
        options: { responsive:true, maintainAspectRatio:false, plugins:{ legend:{ labels:{ boxWidth:12, font:{ size:10 } } } } }
      });
    }
  }, [tab, pmcArr.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendIA = async () => {
    if (!iaInput.trim()) return;
    const msg = iaInput.trim();
    setIaMsgs(m => [...m, { role:'user', text:msg }]);
    setIaInput('');
    setIaLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/ia/chat`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({ message: msg, athleteId, ctl, atl, tsb })
      });
      const data = await res.json();
      setIaMsgs(m => [...m, { role:'assistant', text: data.reply || data.message || 'Sin respuesta' }]);
    } catch(e) {
      setIaMsgs(m => [...m, { role:'assistant', text:'Error al conectar con la IA.' }]);
    }
    setIaLoading(false);
  };

  const tabs = [
    { key:'dashboard', label:`📊 ${L.tabs.dashboard}` },
    { key:'calendar',  label:`📅 ${L.tabs.calendar}` },
    { key:'profile',   label:`👤 ${L.tabs.profile}` },
    { key:'pmc',       label:`📈 ${L.tabs.pmc}` },
    { key:'zones',     label:`❤️ ${L.tabs.zones}` },
    { key:'wellness',  label:`🧘 ${L.tabs.wellness}` },
    { key:'ia',        label:`🤖 ${L.tabs.ia}` },
    { key:'banister',  label:`📉 ${L.tabs.banister}` },
    { key:'competitions', label:`🏆 ${L.tabs.competitions}` },
  ];

  const tsbColor = typeof tsb === 'number' ? (tsb > 0 ? '#16a34a' : tsb < -20 ? '#dc2626' : '#d97706') : '#6b7280';
  const score = wellnessScore();

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', background:'#f4f6f9' }}>

      {/* TABS */}
      <div style={{ display:'flex', gap:0, borderBottom:'1px solid #e5e7eb', background:'#fff', flexShrink:0 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding:'11px 18px', fontSize:12, fontWeight:600,
            color: tab === t.key ? ORANGE : '#6b7280',
            borderBottom: tab === t.key ? `2px solid ${ORANGE}` : '2px solid transparent',
            border:'none', background:'transparent', cursor:'pointer', fontFamily:'inherit',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* CONTENT */}
      <div style={{ flex:1, overflowY:'auto', padding:'16px 20px', display:'flex', flexDirection:'column', gap:16 }}>

        {/* ── DASHBOARD ── */}
        {tab === 'dashboard' && (
          <>
            <div style={{ background:'#fff', border:'0.5px solid #e5e7eb', borderRadius:12, padding:'14px 16px' }}>
              <div style={{ fontSize:11, fontWeight:600, color:'#374151', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:10 }}>
                Estado actual · Temporada
              </div>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                <MetricCard label="CTL (Fitness)" value={ctl} color="#1d4ed8" />
                <MetricCard label="ATL (Fatiga)"  value={atl} color="#ef4444" />
                <MetricCard label="TSB (Forma)"   value={tsb} color={tsbColor} />
                <MetricCard label="TSS Total"     value={tssTotal > 0 ? (tssTotal/1000).toFixed(1)+'k' : '-'} color="#374151" />
              </div>
              {typeof tsb === 'number' && (
                <div style={{ marginTop:10 }}>
                  <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'4px 10px', borderRadius:99, fontSize:11, fontWeight:600,
                    background: tsb > 0 ? '#f0fdf4' : '#fef2f2', color: tsbColor }}>
                    {tsb > 0 ? L.formPositive : tsb < -20 ? L.formFatigue : L.formNeutral}
                  </span>
                </div>
              )}
            </div>
            <div style={{ background:'#fff', border:'0.5px solid #e5e7eb', borderRadius:12, padding:'14px 16px' }}>
              <div style={{ fontSize:11, fontWeight:600, color:'#374151', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:10 }}>
                {L.weeklyTss}}
              </div>
              <div style={{ display:'flex', gap:8, marginBottom:10, flexWrap:'wrap' }}>
                {Object.entries(SPORT_COLORS).slice(0,5).map(([s,c]) => (
                  <span key={s} style={{ fontSize:10, color:c, fontWeight:600 }}>■ {L[s] || s}</span>
                ))}
              </div>
              <div style={{ position:'relative', height:200 }}>
                <canvas ref={weeklyRef} />
              </div>
            </div>

            <div style={{ background:'#fff', border:'0.5px solid #e5e7eb', borderRadius:12, padding:'14px 16px' }}>
              <div style={{ fontSize:11, fontWeight:600, color:'#374151', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:10 }}>
                {L.seasonSummary}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:10 }}>
                <MetricCard label={L.kmRun}     value={seasonTotals.runKm > 0 ? seasonTotals.runKm.toLocaleString('es-CL', {maximumFractionDigits:0}) : '-'} color="#9333ea" />
                <MetricCard label={L.kmBike}      value={seasonTotals.bikeKm > 0 ? seasonTotals.bikeKm.toLocaleString('es-CL', {maximumFractionDigits:0}) : '-'} color="#16a34a" />
                <MetricCard label={L.mSwim}       value={seasonTotals.swimM > 0 ? seasonTotals.swimM.toLocaleString('es-CL', {maximumFractionDigits:0}) : '-'} color="#2563eb" />
                <MetricCard label={L.hoursRun}   value={seasonTotals.runH > 0 ? Math.round(seasonTotals.runH)+'h' : '-'} color="#9333ea" />
                <MetricCard label={L.hoursBike}      value={seasonTotals.bikeH > 0 ? Math.round(seasonTotals.bikeH)+'h' : '-'} color="#16a34a" />
                <MetricCard label={L.hoursSwim}  value={seasonTotals.swimH > 0 ? Math.round(seasonTotals.swimH)+'h' : '-'} color="#2563eb" />
                <MetricCard label={L.hoursStrength}    value={seasonTotals.strengthH > 0 ? Math.round(seasonTotals.strengthH)+'h' : '-'} color="#f59e0b" />
              </div>
            </div>
          </>
        )}

        {/* ── CALENDARIO ── */}
        {tab === 'calendar' && (
          <LABCalendarView
            key={`lab-cal-${athleteId}`}
            athleteId={athleteId}
          />
        )}

        {/* ── PERFIL ── */}
        {tab === 'profile' && (
          <AthleteProfile
            key={`lab-prof-${athleteId}`}
            isTrainerView={true}
            athleteId={athleteId}
          />
        )}

        {/* ── PMC ── */}
        {tab === 'pmc' && (
          <>
            <div style={{ background:'#fff', border:'0.5px solid #e5e7eb', borderRadius:12, padding:'14px 16px' }}>
              <div style={{ fontSize:11, fontWeight:600, color:'#374151', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:10 }}>
                {L.pmcTitle}}
              </div>
              <div style={{ position:'relative', height:260 }}>
                <canvas ref={pmcRef} />
              </div>
            </div>
            <div style={{ background:'#fff', border:'0.5px solid #e5e7eb', borderRadius:12, padding:'14px 16px' }}>
              <div style={{ fontSize:11, fontWeight:600, color:'#374151', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:10 }}>
                {L.pmcInterpretation}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontSize:20, fontWeight:600, color:'#1d4ed8' }}>{ctl}</div>
                  <div style={{ fontSize:10, color:'#6b7280', marginTop:2 }}>{L.ctlCurrent}</div>
                </div>
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontSize:20, fontWeight:600, color:'#ef4444' }}>{atl}</div>
                  <div style={{ fontSize:10, color:'#6b7280', marginTop:2 }}>{L.atlCurrent}</div>
                </div>
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontSize:20, fontWeight:600, color:tsbColor }}>{tsb}</div>
                  <div style={{ fontSize:10, color:'#6b7280', marginTop:2 }}>{L.tsbCurrent}</div>
                </div>
              </div>
              {pmcArr.length === 0 && (
                <div style={{ marginTop:12, padding:10, background:'#fffbeb', borderRadius:8, fontSize:12, color:'#92400e' }}>
                  ⚠ Sin datos PMC. Importa actividades y recalcula el PMC.
                </div>
              )}
            </div>
          </>
        )}

        {/* ── ZONAS HR ── */}
        {tab === 'zones' && (() => {
          const zones = hrZonesSummary();
          const hrByS = hrBySport();
          const totalMin = zones.reduce((s, z) => s + z.min, 0);
          return (
            <>
              <div style={{ background:'#fff', border:'0.5px solid #e5e7eb', borderRadius:12, padding:'14px 16px' }}>
                <div style={{ fontSize:11, fontWeight:600, color:'#374151', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:14 }}>
                  {L.hrDistribution}
                </div>
                {totalMin > 0 ? zones.map((z, i) => (
                  <ZoneBar key={z.label} label={z.label} pct={z.pct} min={z.min} color={ZONE_COLORS[i]} />
                )) : (
                  <div style={{ fontSize:12, color:'#9ca3af' }}>Sin datos de zonas HR. Importa actividades con datos de FC.</div>
                )}
                <div style={{ marginTop:10 }}>
                  <div style={{ fontSize:11, fontWeight:600, color:'#374151', marginBottom:6 }}>{L.hrAvgBySport}</div>
                  <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                    {hrByS.map(({ sport, avg }) => (
                      <div key={sport} style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'8px 12px', flex:1, minWidth:80 }}>
                        <div style={{ fontSize:18, fontWeight:600, color: SPORT_COLORS[sport] }}>{avg}</div>
                        <div style={{ fontSize:10, color:'#6b7280', marginTop:2 }}>{L[sport] || sport} (bpm)</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ background:'#fff', border:'0.5px solid #e5e7eb', borderRadius:12, padding:'14px 16px' }}>
                <div style={{ fontSize:11, fontWeight:600, color:'#374151', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:10 }}>
                  {L.hrChart}
                </div>
                <div style={{ position:'relative', height:200 }}>
                  <canvas ref={zonesRef} />
                </div>
              </div>
            </>
          );
        })()}

        {/* ── WELLNESS ── */}
        {tab === 'wellness' && (
          <div style={{ background:'#fff', border:'0.5px solid #e5e7eb', borderRadius:12, padding:'14px 16px' }}>
            <div style={{ fontSize:11, fontWeight:600, color:'#374151', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:14 }}>
              Registro wellness · {athleteName} · hoy
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {[
                { key:'sleep',  label:`😴 ${L.sleepQuality}`, min:1, max:10, unit:'/10' },
                { key:'energy', label:`⚡ ${L.energy}`,          min:1, max:10, unit:'/10' },
                { key:'muscle', label:`💪 ${L.muscleTension}`, min:1, max:10, unit:'/10' },
                { key:'mental', label:`🧠 ${L.mentalState}`,    min:1, max:10, unit:'/10' },
                { key:'hr',     label:`❤️ ${L.restHR}`,        min:35, max:80, unit:' bpm' },
                { key:'stress', label:`😰 ${L.perceivedStress}`, min:1, max:10, unit:'/10' },
              ].map(({ key, label, min, max, unit }) => (
                <div key={key}>
                  <label style={{ fontSize:11, color:'#4b5563', display:'block', marginBottom:6 }}>
                    {label} <strong>{wellness[key]}</strong>{unit}
                  </label>
                  <input type="range" min={min} max={max} value={wellness[key]}
                    onChange={e => setWellness(w => ({ ...w, [key]: Number(e.target.value) }))}
                    style={{ width:'100%', accentColor: ORANGE }} />
                </div>
              ))}
            </div>
            <div style={{ marginTop:14, padding:12, borderRadius:10, border:'0.5px solid #e5e7eb' }}>
              <div style={{ fontSize:11, color:'#9ca3af', marginBottom:6 }}>{L.wellnessScore.toUpperCase()}</div>
              <div style={{ fontSize:28, fontWeight:600, color: score >= 70 ? '#4CAF50' : score >= 50 ? '#f59e0b' : '#ef4444' }}>{score}</div>
              <div style={{ fontSize:11, color: score >= 70 ? '#4CAF50' : score >= 50 ? '#f59e0b' : '#ef4444', marginTop:2 }}>
                {score >= 70 ? (L === L ? '✓ ' + (language === 'en' ? 'Good — continue with plan' : 'Bien — continuar con plan') : '') : score >= 50 ? '→ ' + L.moderate : '⚠ ' + (language === 'en' ? 'Low — adjust load' : 'Bajo — ajustar carga')}
              </div>
              <div style={{ position:'relative', height:120, marginTop:10 }}>
                <canvas ref={wellnessRef} />
              </div>
            </div>
            <WellnessVsCargaCard athleteId={athleteId} athleteName={athleteName} />
          </div>
        )}

        {/* ── IA ── */}
        {tab === 'ia' && (
          <>
            <div style={{ background:'#eff6ff', border:'0.5px solid #bfdbfe', borderRadius:12, padding:'14px 16px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:'#27aae1' }} />
                <span style={{ fontSize:12, fontWeight:600, color:'#1d6fa4' }}>{L.iaTitle}</span>
              </div>
              <div style={{ fontSize:13, color:'#1f2937', lineHeight:1.7 }}>
                <strong>{athleteName}</strong> — {L.iaBasedOn}<br /><br />
                {ctl !== '-' && <><span style={{ display:'inline-block', background:'#1e3a5f', color:'#1d6fa4', fontSize:10, fontWeight:600, padding:'2px 8px', borderRadius:99, margin:2 }}>CTL {ctl}</span>
                <span style={{ display:'inline-block', background:'#1e3a5f', color:'#1d6fa4', fontSize:10, fontWeight:600, padding:'2px 8px', borderRadius:99, margin:2 }}>TSB {tsb}</span><br /><br /></>}
                <strong style={{ color:'#1d6fa4' }}>{L.iaForm}</strong> {ctl !== '-'
                  ? (language === 'en' ? `Current CTL of ${ctl}, with TSB ${tsb > 0 ? 'positive ('+tsb+') — ready to compete.' : 'negative ('+tsb+') — in loading zone.'}` : `CTL actual de ${ctl}, con TSB ${tsb > 0 ? 'positivo ('+tsb+') — apto para competencia.' : 'negativo ('+tsb+') — en zona de carga.'}`)
                  : L.noPmcData
                }
              </div>
            </div>
            <div style={{ background:'#fff', border:'0.5px solid #e5e7eb', borderRadius:12, padding:'14px 16px', marginBottom:12 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                <div style={{ fontSize:11, fontWeight:600, color:'#374151', textTransform:'uppercase', letterSpacing:'.08em' }}>📋 {L.coachReport}</div>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
                    <input type="date" value={reportFrom} onChange={e => setReportFrom(e.target.value)}
                      style={{ border:'1px solid #e5e7eb', borderRadius:6, padding:'5px 8px', fontSize:11, fontFamily:'inherit' }} />
                    <span style={{ fontSize:9, color:'#9ca3af', marginTop:2 }}>{new Date(reportFrom+'T12:00:00').toLocaleDateString(language === 'en' ? 'en-US' : 'es-CL', { day:'2-digit', month:'2-digit', year:'numeric' })}</span>
                  </div>
                  <span style={{ fontSize:11, color:'#6b7280' }}>→</span>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
                    <input type="date" value={reportTo} onChange={e => setReportTo(e.target.value)}
                      style={{ border:'1px solid #e5e7eb', borderRadius:6, padding:'5px 8px', fontSize:11, fontFamily:'inherit' }} />
                    <span style={{ fontSize:9, color:'#9ca3af', marginTop:2 }}>{new Date(reportTo+'T12:00:00').toLocaleDateString(language === 'en' ? 'en-US' : 'es-CL', { day:'2-digit', month:'2-digit', year:'numeric' })}</span>
                  </div>
                  <button onClick={generateReport} disabled={reportLoading}
                    style={{ background:'#1e3a5f', color:'#fff', border:'none', borderRadius:8, padding:'8px 16px', fontSize:12, cursor:'pointer', fontFamily:'inherit', opacity: reportLoading ? 0.7 : 1 }}>
                    {reportLoading ? `⏳ ${L.generating}` : `🤖 ${L.generateReport}`}
                  </button>
                  {coachReport && (
                    <button onClick={sendReportEmail} disabled={emailSending}
                      style={{ background:'#16a34a', color:'#fff', border:'none', borderRadius:8, padding:'8px 16px', fontSize:12, cursor:'pointer', fontFamily:'inherit', opacity: emailSending ? 0.7 : 1 }}>
                      {emailSending ? '⏳...' : `📧 ${language === 'en' ? 'Email to athlete' : 'Enviar al atleta'}`}
                    </button>
                  )}
                </div>
                {emailSent && <div style={{ fontSize:11, color:'#16a34a', marginTop:4 }}>{emailSent}</div>}
              </div>
              {coachReport && (
                <div style={{ fontSize:13, color:'#1f2937', lineHeight:1.8, whiteSpace:'pre-wrap', background:'#f8fafc', borderRadius:8, padding:'14px 16px', border:'0.5px solid #e5e7eb', maxHeight:500, overflowY:'auto' }}>
                  {coachReport.split('**').map((part, i) => i % 2 === 1 ? <strong key={i}>{part}</strong> : part)}
                </div>
              )}
              {!coachReport && !reportLoading && (
                <div style={{ fontSize:12, color:'#9ca3af', textAlign:'center', padding:'16px 0' }}>
                  {L.reportPlaceholder}
                </div>
              )}
            </div>

            <div style={{ background:'#fff', border:'0.5px solid #e5e7eb', borderRadius:12, padding:'14px 16px' }}>
              <div style={{ fontSize:11, fontWeight:600, color:'#374151', marginBottom:10 }}>{L.askIA}</div>
              <div style={{ display:'flex', flexDirection:'column', gap:8, minHeight:60, marginBottom:10 }}>
                {iaMsgs.map((m, i) => (
                  <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth:'80%',
                    background: m.role === 'user' ? PU : '#f3f4f6', color: m.role === 'user' ? '#fff' : '#111827',
                    borderRadius:10, padding:'8px 12px', fontSize:12 }}>
                    {m.text}
                  </div>
                ))}
                {iaLoading && <div style={{ fontSize:12, color:'#9ca3af' }}>Pensando...</div>}
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <input value={iaInput} onChange={e => setIaInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendIA()}
                  placeholder="Ej: ¿Puede competir este fin de semana?"
                  style={{ flex:1, background:'#f8f9fa', border:'0.5px solid #e5e7eb', borderRadius:8, padding:'10px 12px', fontSize:13, outline:'none', fontFamily:'inherit' }} />
                <button onClick={sendIA} style={{ background:PU, color:'#fff', border:'none', borderRadius:8, padding:'10px 16px', fontSize:13, cursor:'pointer' }}>→</button>
              </div>
              <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
                {[L.tabs.ia === 'AI' ? 'Can they compete this weekend?' : '¿Puede competir este fin de semana?','¿Qué ajustar en Z3?','Resumen de la semana'].map(q => (
                  <button key={q} onClick={() => { setIaInput(q); }}
                    style={{ background:'#f8f9fa', border:'0.5px solid #e5e7eb', color:'#4b5563', borderRadius:99, padding:'5px 12px', fontSize:11, cursor:'pointer', fontFamily:'inherit' }}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── BANISTER ── */}
        {tab === 'banister' && (
          <>
            <div style={{ background:'#fff', border:'0.5px solid #e5e7eb', borderRadius:12, padding:'14px 16px' }}>
              <div style={{ fontSize:11, fontWeight:600, color:'#374151', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:10,
                display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:6 }}>
                <span>{L.banisterTitle}</span>
                <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                  {[['#2a78d6','CTL'],['#e34948','ATL'],['#1baf7a','TSB']].map(([c,l]) => (
                    <span key={l} style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'#6b7280' }}>
                      <span style={{ width:16, height:2, background:c, display:'inline-block' }} />{l}
                    </span>
                  ))}
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,minmax(0,1fr))', gap:10, margin:'10px 0 14px' }}>
                <MetricCard label="CTL Fitness" value={ctl} color="#1d4ed8" />
                <MetricCard label="ATL Fatiga"  value={atl} color="#ef4444" />
                <MetricCard label="TSB Forma"   value={tsb} color={tsbColor} />
              </div>
              <div style={{ position:'relative', height:260 }}>
                <canvas ref={banisterRef} />
              </div>
            </div>
            <div style={{ padding:'12px 14px', background:'#f0fdf4', borderRadius:12, border:'0.5px solid #bbf7d0', fontSize:12, color:'#166534', lineHeight:1.7 }}>
              {pmcArr.length > 0
                ? `${tsb > 0 ? '✓ ' + L.positiveForm : '→ ' + L.monitorForm} CTL: ${ctl} | ATL: ${atl} | TSB: ${tsb}`
                : L.noBanisterData
              }
            </div>
          </>
        )}
        {/* ── COMPETENCIAS ── */}
        {tab === 'competitions' && (
          <div style={{ background:'#fff', border:'0.5px solid #e5e7eb', borderRadius:12, padding:'14px 16px' }}>
            <div style={{ fontSize:11, fontWeight:600, color:'#374151', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:12 }}>
              🏆 {L.competitionsTitle}}
            </div>
            {(() => {
              const comps = competitions;
              if (!comps.length) return <div style={{ fontSize:13, color:'#9ca3af', padding:'20px 0', textAlign:'center' }}>{L.noCompetitions}</div>;
              const sorted = [...comps].sort((a,b) => new Date(a.date) - new Date(b.date));
              const prioColor = { A:'#dc2626', B:'#f59e0b', C:'#6b7280' };
              const prioBg = { A:'#fef2f2', B:'#fef3c7', C:'#f9fafb' };
              return (
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead>
                    <tr style={{ borderBottom:'1px solid #e5e7eb' }}>
                      {[L.compDate,L.compEvent,L.compSport,L.compDistance,L.compPriority,L.compCity,L.compTarget].map(h => (
                        <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontSize:11, color:'#6b7280', fontWeight:600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((c, i) => {
                      const comp = c.competition || {};
                      const prio = comp.priority || '-';
                      return (
                        <tr key={c._id || i} style={{ borderBottom:'1px solid #f3f4f6' }}>
                          <td style={{ padding:'8px 10px', whiteSpace:'nowrap' }}>{new Date(c.date).toLocaleDateString('es-CL', { day:'numeric', month:'short', year:'numeric' })}</td>
                          <td style={{ padding:'8px 10px', fontWeight:600 }}>{comp.eventName || c.title || '-'}</td>
                          <td style={{ padding:'8px 10px' }}>{comp.sport || '-'}</td>
                          <td style={{ padding:'8px 10px' }}>{comp.distance || '-'}</td>
                          <td style={{ padding:'8px 10px' }}>
                            <span style={{ padding:'2px 8px', borderRadius:12, fontSize:11, fontWeight:700, color: prioColor[prio] || '#374151', background: prioBg[prio] || '#f9fafb' }}>{prio}</span>
                          </td>
                          <td style={{ padding:'8px 10px' }}>{comp.city || '-'}</td>
                          <td style={{ padding:'8px 10px' }}>{comp.targetTime || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              );
            })()}
          </div>
        )}

      </div>
    </div>
  );
}
